<template>
  <div class="space-center forgot-password">
    <!-- 标题 -->
    <h2 class="forgot-title">忘记密码</h2>

    <div class="form">
      <!-- 手机号码 -->
      <div class="input-group" :class="{ 'has-error': touched.phone && errors.phone }">
        <input
          ref="phoneInputRef"
          v-model="formData.phone"
          type="tel"
          placeholder="请输入手机号"
          class="main-input"
          maxlength="11"
          @blur="markTouched('phone'); checkPhone()"
          @input="checkPhone"
        />
        <div class="svg">
          <svg-icon name="手机" color="svg" />
        </div>
        <div v-if="touched.phone && errors.phone" class="error-message">
          {{ errors.phone }}
        </div>
      </div>

      <!-- 图形验证码 -->
      <div class="input-group captcha-group" :class="{ 'has-error': touched.captcha && errors.captcha }">
        <input
          ref="captchaInputRef"
          v-model="formData.captcha"
          type="text"
          placeholder="请输入图形验证码"
          class="main-input"
          maxlength="6"
          @blur="markTouched('captcha'); checkCaptcha()"
          @input="checkCaptcha"
        />
        <div class="captcha-img" @click="refreshCaptcha" title="点击刷新">
          <img v-if="captchaImgUrl" :src="captchaImgUrl" alt="验证码" />
          <div v-else class="captcha-placeholder">{{ captchaText }}</div>
        </div>
        <div v-if="touched.captcha && errors.captcha" class="error-message">
          {{ errors.captcha }}
        </div>
      </div>

      <!-- 短信验证码 -->
      <div class="input-group sms-group" :class="{ 'has-error': touched.smsCode && errors.smsCode }">
        <input
          ref="smsCodeInputRef"
          v-model="formData.smsCode"
          type="text"
          placeholder="请输入短信验证码"
          class="main-input"
          maxlength="6"
          @blur="markTouched('smsCode'); checkSmsCode()"
          @input="checkSmsCode"
        />
        <button
          class="send-sms-btn"
          :disabled="smsCooldown > 0 || !canSendSms || isTacLoading"
          @click="sendSmsCode"
        >
          {{ smsCooldown > 0 ? `${smsCooldown}s` : isTacLoading ? '校验中...' : '发送验证码' }}
        </button>
        <div id="captcha-box-forget" class="tac-container"></div>
        <div v-if="touched.smsCode && errors.smsCode" class="error-message">
          {{ errors.smsCode }}
        </div>
      </div>

      <!-- 新密码 -->
      <div class="input-group" :class="{ 'has-error': touched.newPassword && errors.newPassword }">
        <input
          ref="newPasswordInputRef"
          v-model="formData.newPassword"
          :type="showNewPassword ? 'text' : 'password'"
          placeholder="请输入新密码（6-20位）"
          class="main-input"
          autocorrect="off"
          autocapitalize="off"
          @blur="markTouched('newPassword'); checkNewPassword()"
          @input="checkNewPassword"
        />
        <div class="svg password-icon" @click="toggleNewPassword">
          <svg-icon :name="showNewPassword ? '解锁' : '密码'" color="svg" />
        </div>
        <div v-if="touched.newPassword && errors.newPassword" class="error-message">
          {{ errors.newPassword }}
        </div>
      </div>

      <!-- 确认新密码 -->
      <div class="input-group" :class="{ 'has-error': touched.confirmPassword && errors.confirmPassword }">
        <input
          ref="confirmPasswordInputRef"
          v-model="formData.confirmPassword"
          :type="showConfirmPassword ? 'text' : 'password'"
          placeholder="请确认新密码"
          class="main-input"
          autocorrect="off"
          autocapitalize="off"
          @blur="markTouched('confirmPassword'); checkConfirmPassword()"
          @input="checkConfirmPassword"
        />
        <div class="svg password-icon" @click="toggleConfirmPassword">
          <svg-icon :name="showConfirmPassword ? '解锁' : '密码'" color="svg" />
        </div>
        <div v-if="touched.confirmPassword && errors.confirmPassword" class="error-message">
          {{ errors.confirmPassword }}
        </div>
      </div>

      <!-- 确认修改按钮 -->
      <button class="Signup-Btn confirm-btn" :disabled="loading" @click="handleSubmit">
        <svg-icon v-if="loading" class="icon-loading" size="18" name="加载" color="#fff" />
        <span class="button-text">{{ loading ? '修改中...' : '确认修改' }}</span>
      </button>

      <!-- 取消 -->
      <div class="cancel-link" @click="goToLogin">取消</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'd3components/src/utils/tools'
import { getLuatCaptcha, getSendCode, showLuatTacCaptcha } from '../api'
import { ApiUserLuat } from 'd3components'

defineOptions({ name: 'luatos-forgot-password' })

const { luatResetPassword } = ApiUserLuat
const router = useRouter()

// ===== 输入框引用 =====
const phoneInputRef = ref<HTMLInputElement | null>(null)
const captchaInputRef = ref<HTMLInputElement | null>(null)
const smsCodeInputRef = ref<HTMLInputElement | null>(null)
const newPasswordInputRef = ref<HTMLInputElement | null>(null)
const confirmPasswordInputRef = ref<HTMLInputElement | null>(null)

// ==================== 状态管理 ====================
const loading = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)
const isTacLoading = ref(false)
const captchaImgUrl = ref('')
const captchaText = ref('M4B6')
const captchaId = ref('')
const smsCooldown = ref(0)

let cooldownTimer: any = null
let currentTac: any = null

// 表单数据
const formData = ref({
  phone: '',
  captcha: '',
  smsCode: '',
  newPassword: '',
  confirmPassword: '',
})

// 字段触摸状态
const touched = ref({
  phone: false,
  captcha: false,
  smsCode: false,
  newPassword: false,
  confirmPassword: false,
})

// 字段错误信息
const errors = ref({
  phone: '',
  captcha: '',
  smsCode: '',
  newPassword: '',
  confirmPassword: '',
})

// ==================== 计算属性 ====================
const canSendSms = computed(() => {
  return formData.value.phone && 
         /^1[3456789]\d{9}$/.test(formData.value.phone) && 
         formData.value.captcha
})

// ==================== 页面导航 ====================
const goToLogin = () => {
  router.push('/login')
}

// ==================== 密码显示切换 ====================
const toggleNewPassword = () => {
  showNewPassword.value = !showNewPassword.value
}

const toggleConfirmPassword = () => {
  showConfirmPassword.value = !showConfirmPassword.value
}

// ==================== 图形验证码 ====================
const refreshCaptcha = async () => {
  try {
    const res = await getLuatCaptcha()
    if (res.code==0) {
      captchaImgUrl.value = res.value.img
      captchaId.value = res.value.captcha_id;
    } else {
      toast(res?.msg || '获取验证码失败', 3)
    }
  } catch (err: any) {
    toast(err?.message || '获取验证码异常', 3)
  }
}

// ==================== 字段验证（实时） ====================
const markTouched = (field: keyof typeof touched.value) => {
  touched.value[field] = true
}

const checkPhone = () => {
  if (!touched.value.phone) return
  
  if (!formData.value.phone) {
    errors.value.phone = '请输入手机号'
  } else if (!/^1[3456789]\d{9}$/.test(formData.value.phone)) {
    errors.value.phone = '请输入正确的手机号'
  } else {
    errors.value.phone = ''
  }
}

const checkCaptcha = () => {
  if (!touched.value.captcha) return
  
  if (!formData.value.captcha) {
    errors.value.captcha = '请输入图形验证码'
  } else if (formData.value.captcha.length < 4) {
    errors.value.captcha = '请输入完整验证码'
  } else {
    errors.value.captcha = ''
  }
}

const checkSmsCode = () => {
  if (!touched.value.smsCode) return
  
  if (!formData.value.smsCode) {
    errors.value.smsCode = '请输入短信验证码'
  } else if (formData.value.smsCode.length < 6) {
    errors.value.smsCode = '请输入正确格式验证码'
  } else {
    errors.value.smsCode = ''
  }
}

const checkNewPassword = () => {
  if (!touched.value.newPassword) return
  
  if (!formData.value.newPassword) {
    errors.value.newPassword = '请输入新密码'
  } else if (formData.value.newPassword.length < 6) {
    errors.value.newPassword = '密码至少6位'
  } else if (formData.value.newPassword.length > 20) {
    errors.value.newPassword = '密码最多20位'
  } else {
    errors.value.newPassword = ''
  }
}

const checkConfirmPassword = () => {
  if (!touched.value.confirmPassword) return
  
  if (!formData.value.confirmPassword) {
    errors.value.confirmPassword = '请确认新密码'
  } else if (formData.value.confirmPassword !== formData.value.newPassword) {
    errors.value.confirmPassword = '两次密码不一致'
  } else {
    errors.value.confirmPassword = ''
  }
}

// ==================== 短信验证码 ====================
const validateBeforeSendSms = (): boolean => {
  touched.value.phone = true
  touched.value.captcha = true
  
  checkPhone()
  checkCaptcha()
  
  if (errors.value.phone) {
    toast(errors.value.phone, 3)
    return false
  }
  
  if (errors.value.captcha) {
    toast(errors.value.captcha, 3)
    return false
  }
  
  return true
}

const startCooldown = () => {
  smsCooldown.value = 60
  
  if (cooldownTimer) {
    clearInterval(cooldownTimer)
    cooldownTimer = null
  }
  
  cooldownTimer = setInterval(() => {
    smsCooldown.value--
    if (smsCooldown.value <= 0) {
      clearInterval(cooldownTimer)
      cooldownTimer = null
    }
  }, 1000)
}

const sendSmsCode = async () => {
  if (!validateBeforeSendSms()) return
  if (smsCooldown.value > 0 || isTacLoading.value) return

  isTacLoading.value = true
  currentTac?.destroyWindow?.()

  await showLuatTacCaptcha({
    bindEl: '#captcha-box-forget',
    phone: formData.value.phone,
    onSuccess: async (response: any) => {
      try {
        const res = await getSendCode({
          captchaId: captchaId.value,
          phone: formData.value.phone,
          captchaCode: formData.value.captcha,
          auth2: response?.auth2,
        })
        
        if (res?.isSuc || res?.code === 0) {
          toast('验证码已发送，请注意查收', 1)
          startCooldown()
        } else {
          toast(res?.obj || res?.value || res?.msg || '发送验证码失败', 2)
          refreshCaptcha()
        }
      } catch (err: any) {
        toast(err?.message || '发送验证码异常', 3)
        refreshCaptcha()
      } finally {
        isTacLoading.value = false
        currentTac = null
      }
    },
    onClose: () => {
      isTacLoading.value = false
      currentTac = null
    },
    onLoadError: (err: any) => {
      toast(err?.message || '图形验证码加载失败', 3)
      isTacLoading.value = false
      currentTac = null
    },
  }).then((tac: any) => {
    currentTac = tac
  }).catch(() => {})
}

// ==================== 表单提交验证 ====================
const validateAllFields = (): boolean => {
  // 按顺序检查每个字段，遇到第一个错误就停止
  
  // 检查手机号
  if (!touched.value.phone) touched.value.phone = true
  checkPhone()
  if (errors.value.phone) {
    toast(errors.value.phone, 3)
    nextTick(() => phoneInputRef.value?.focus())
    return false
  }
  
  // 检查图形验证码
  if (!touched.value.captcha) touched.value.captcha = true
  checkCaptcha()
  if (errors.value.captcha) {
    toast(errors.value.captcha, 3)
    nextTick(() => captchaInputRef.value?.focus())
    return false
  }
  
  // 检查短信验证码
  if (!touched.value.smsCode) touched.value.smsCode = true
  checkSmsCode()
  if (errors.value.smsCode) {
    toast(errors.value.smsCode, 3)
    nextTick(() => smsCodeInputRef.value?.focus())
    return false
  }
  
  // 检查新密码
  if (!touched.value.newPassword) touched.value.newPassword = true
  checkNewPassword()
  if (errors.value.newPassword) {
    toast(errors.value.newPassword, 3)
    nextTick(() => newPasswordInputRef.value?.focus())
    return false
  }
  
  // 检查确认密码
  if (!touched.value.confirmPassword) touched.value.confirmPassword = true
  checkConfirmPassword()
  if (errors.value.confirmPassword) {
    toast(errors.value.confirmPassword, 3)
    nextTick(() => confirmPasswordInputRef.value?.focus())
    return false
  }
  
  return true
}

// ==================== 提交重置密码 ====================
const handleSubmit = async () => {
  if (!validateAllFields()) return

  loading.value = true
  
  try {
    const res = await luatResetPassword({
      phone: formData.value.phone,
      newPassword: formData.value.newPassword,
      vcode: formData.value.smsCode,
    })
    
    if (res.code==0) {
      toast('修改成功', 1)
      setTimeout(() => {
        router.replace('/login')
      }, 1000)
    } else {
      toast(res.value || '修改失败', 3)
    }
  } catch (err: any) {
    toast('修改失败请重试！', 3)
  } finally {
    loading.value = false
  }
}

// ==================== 生命周期 ====================
onMounted(() => {
  refreshCaptcha()
  
  // ===== 自动聚焦第一个输入框 =====
  phoneInputRef.value?.focus()
})

onUnmounted(() => {
  if (cooldownTimer) {
    clearInterval(cooldownTimer)
    cooldownTimer = null
  }
  currentTac?.destroyWindow?.()
  currentTac = null
})
</script>

<style scoped lang="scss">
// 使用与登录/注册页面相同的样式变量

$primary-color-login-text: rgba(57, 140, 254, 1);

// ===== 科技感配色 =====
$primary-gradient: linear-gradient(135deg, rgba(57, 140, 254, 1) 0%, rgba(26, 111, 220, 1) 100%);
// $primary-gradient: $primary-color-login-text;
 //$accent-color: ;
$accent-color: $primary-color-login-text;
$text-secondary: #8a94a6;
$spacing-md: 1.25rem;
$spacing-lg: 1.5rem;
$spacing-xl: 1.875rem;
$radius-sm: 4px;
$radius-full: 54px;
$shadow-xs: 0 2px 8px rgba(0, 0, 0, 0.02);
$shadow-sm: 0 2px 10px rgba(57, 140, 254, 0.3);
$shadow-md: 0 4px 16px rgba(57, 140, 254, 0.3);
$shadow-lg: 0 6px 20px rgba(57, 140, 254, 0.45);
$shadow-hover: 0 4px 16px rgba($accent-color, 0.08);
$shadow-focus: 0 0 0 4px rgba($accent-color, 0.1),
  0 6px 20px rgba($accent-color, 0.12);
$font-size-sm: 0.8rem;
$font-size-lg: 1rem;
$font-weight-medium: 500;
$font-weight-semibold: 600;
$font-weight-bold: 900;
$transition-base: 0.3s ease;
$transition-md: 0.35s cubic-bezier(0.4, 0, 0.2, 1);
$transition-lg: 0.4s cubic-bezier(0.4, 0, 0.2, 1);
$transition-xl: 0.5s ease;

.forgot-password {
  width: 100vw;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #fff !important;

  h2 {
    background: $primary-gradient;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: relative;
    letter-spacing: 0.1em;
  }

  .forgot-title {
    font-weight: $font-weight-bold;
    margin-bottom: 1.3rem;
  }

  :deep(.form) {
    padding: $spacing-lg 0;
    width: 80%;

    .input-group {
      position: relative;
      margin-top: $spacing-xl;
      background: #fafbfc;
      border: 2px solid #e8ecf2 !important;
      border-radius: $radius-full;
      padding: 14px 54px;
      transition: all $transition-md;
      box-shadow: $shadow-xs;
      outline: none;

      &:hover {
        border-color: rgba($accent-color, 0.4);
        background: #fff;
        box-shadow: $shadow-hover;
      }

      &:focus-within {
        border-color: $accent-color !important;
        background: #fff;
        box-shadow: $shadow-focus;

        .svg {
          opacity: 1;
          color: $accent-color;
        }
      }

      // 错误状态样式
      &.has-error {
        border-color: #f56c6c !important;
        background: #fef0f0;

        &:hover {
          border-color: #f56c6c !important;
          background: #fef0f0;
        }

        &:focus-within {
          border-color: #f56c6c !important;
          background: #fef0f0;
          box-shadow: 0 0 0 4px rgba(245, 108, 108, 0.1);

          .svg {
            color: #f56c6c;
          }
        }

        .svg {
          color: #f56c6c;
        }
      }

      // 错误提示文字（绝对定位，不占据高度）
      .error-message {
        position: absolute;
        left: 54px;
        right: 54px;
        top: 100%;
        margin-top: 8px;
        font-size: 24px;
        color: #f56c6c;
        line-height: 1.2;
        font-weight: 500;
        animation: slideDown 0.3s ease;
        z-index: 10;
      }

      .main-input {
        padding: 0.5rem 0.5rem 0.5rem 0.25rem;
        border: none;
        background: transparent !important;
        font-size: $font-size-lg;
        font-weight: $font-weight-medium;
        color: #2d3748;
        width: 100%;
        transition: all $transition-base;

        &::placeholder {
          color: $text-secondary;
        }

        &:focus {
          outline: none;
        }
      }

      .svg {
        position: absolute;
        right: 0.9rem;
        top: 45%;
        transform: translateY(-50%);
        font-size: 1.5rem;
        color: $primary-color-login-text;
        opacity: 0.7;
        transition: all $transition-md;

        &.password-icon {
          cursor: pointer;

          &:hover {
            opacity: 1;
            transform: translateY(-50%) scale(1.1);
          }
        }
      }
    }

    // 图形验证码行：input 占左侧，验证码图片在右侧
    .captcha-group {
      padding-right: 0;
      display: flex;
      align-items: center;

      .main-input {
        flex: 1;
        padding-right: 0.5rem;
      }

      .captcha-img {
        flex-shrink: 0;
        width: 130px;
        height: 100%;
        cursor: pointer;
        border-left: 2px solid #e8ecf2;
        // overflow: hidden;
        display: flex;
        margin-right: 20px;
        align-items: center;
        justify-content: center;
        border-radius: 0 $radius-full $radius-full 0;
        transition: opacity $transition-base;

        &:hover {
          opacity: 0.8;
        }

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .captcha-placeholder {
          font-family: 'Courier New', monospace;
          font-size: 1.15rem;
          font-weight: $font-weight-bold;
          letter-spacing: 0.25em;
          // 彩色渐变文字
          background: linear-gradient(120deg, #3a7bd5 0%, #e96c5a 40%, #3a7bd5 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          // 背景：网格噪点质感
          background-color: #eef5ff;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          user-select: none;
          position: relative;
          // 模拟噪点纹理
          background-image:
            linear-gradient(120deg, #3a7bd5 0%, #e96c5a 40%, #3a7bd5 100%),
            repeating-linear-gradient(45deg,
              rgba(57, 123, 213, 0.04) 0px,
              rgba(57, 123, 213, 0.04) 1px,
              transparent 1px,
              transparent 8px);
          text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.6), -1px -1px 2px rgba(0, 0, 0, 0.08);
        }
      }
    }

    // 短信验证码行：input 占左侧，发送按钮在右侧
    .sms-group {
      padding-right: 0;
      display: flex;
      align-items: center;

      .main-input {
        flex: 1;
        padding-right: 0.5rem;
      }

      .send-sms-btn {
        flex-shrink: 0;
        height: 100%;
        min-width: 110px;
        padding: 0 12px;
        background: transparent;
        color: $accent-color;
        border: none;
        border-left: 2px solid #e8ecf2;
        border-radius: 0 $radius-full $radius-full 0;
        font-size: 0.85rem;
        font-weight: $font-weight-semibold;
        cursor: pointer;
        transition: all $transition-base;
        white-space: nowrap;
        letter-spacing: 0.02em;

        &:hover:not(:disabled) {
          background: rgba($accent-color, 0.06);
        }

        &:disabled {
          color: #b0b8c4;
          cursor: not-allowed;
        }
      }

      .tac-container {
        position: absolute;
        right: 0;
        bottom: calc(100% + 8px);
        z-index: 9999;
        background: transparent;
        transform-origin: right bottom;
      }
    }

    // 确认修改主按钮
    .Signup-Btn {
      user-select: none;

      &.confirm-btn {
        margin-top: $spacing-xl;
      }

      background: $primary-gradient;
      border: none;
      padding: 0 $spacing-lg;
      height: 88px;
      min-height: 88px;
      font-size: $font-size-lg;
      font-weight: $font-weight-semibold;
      letter-spacing: 0.15em;
      box-shadow: $shadow-md;
      transition: all $transition-lg;
      position: relative;
      overflow: hidden;
      border-radius: $radius-full;
      color: #fff;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
        transition: left $transition-xl;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: $shadow-lg;

        &::before {
          left: 100%;
        }
      }

      &:active {
        transform: translateY(0) scale(0.98);
        box-shadow: $shadow-sm;
      }

      &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
        transform: none;
      }

      .button-text {
        margin-left: 10px;
      }
    }

    // 取消链接
    .cancel-link {

      user-select: none;
      margin-top: $spacing-md;
      width: 100%;
      height: 88px;
      border: 2px solid $accent-color;
      background: transparent;
      color: $accent-color;
      font-size: $font-size-lg;
      font-weight: $font-weight-semibold;
      letter-spacing: 0.15em;
      border-radius: $radius-full;
      cursor: pointer;
      transition: all $transition-base;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        opacity: 0.75;
      }
    }
  }
}

:global(#tianai-captcha-parent) {
  background: #fff;
}
</style>
